import { DateTime } from '@common/data-models/complex-types/date-time';
import moment from 'moment-timezone';

export class DataType {
    public static NUMBER: number = 1;
    public static STRING: number = 2;
    public static DATE: number = 3;
    public static OBJECT: number = 4;
    public static BOOL: number = 5;
    public static PUBLISHEDDATE: number = 6;
    public static DISCOVERDATE: number = 7;
    public static TABLE: number = 100;
    public static MULTIVALUE: number = 101;
    public static LOOKUPLIST: number = 102;

    private static DATE_FORMAT: RegExp = /^([0-9]{4})[-]?([0-9]{2})[-]?([0-9]{2})T([0-9]{2})[:]?([0-9]{2})[:]?([0-9]{2})\.?([0-9]+)?([Z])?$/;

    public static guess(val: any): number {
        if (val instanceof Date) return DataType.DATE;

        const typeStr = typeof val;
        switch (typeStr) {
            case 'number':
                return DataType.NUMBER;
            case 'boolean':
                return DataType.BOOL;
            case 'string':
                return val.match(DataType.DATE_FORMAT) ? DataType.DATE : DataType.STRING;
            case 'object':
                return DataType.OBJECT;
        }
    }

    public static getTypeName(dataType: number) {
        switch (dataType) {
            case DataType.NUMBER:
                return 'NUMBER';
            case DataType.STRING:
                return 'STRING';
            case DataType.DATE:
                return 'DATE';
            case DataType.OBJECT:
                return 'OBJECT';
            case DataType.BOOL:
                return 'BOOL';
            default:
                throw `Unable to get type name for type id ${dataType}`;
        }
    }

    private static toNumber(v: string): number {
        let tmp = v;
        while (tmp.length > 1 && tmp.charAt(0) === '0') {
            tmp = tmp.substring(1, tmp.length);
        }
        return parseInt(tmp);
    }

    public static fromRaw(rawValue: any, targetType: number) {
        if (rawValue === null || typeof rawValue === 'undefined') return null;

        const rawValueType = typeof rawValue;
        switch (targetType) {
            case DataType.NUMBER:
                switch (rawValueType) {
                    case 'string':
                        return parseInt(rawValue);
                    case 'number':
                        return rawValue;
                    default:
                        throw `Unable to convert value '${rawValue === null ? 'null' : rawValue.toString()}' to type ${DataType.getTypeName(targetType)} (JS type: ${rawValueType})`;
                }
            case DataType.STRING:
                if (rawValue instanceof Date) return rawValue.toString();
                switch (rawValueType) {
                    case 'number':
                        return rawValue.toString();
                    case 'boolean':
                        return rawValue.toString();
                    case 'string':
                        return rawValue
                            .replace(/~C13~~C10~/g, '\n')
                            .replace(/~C13~/g, '\n')
                            .replace(/~C10~/g, '\n')
                            .replace(/~C9~/g, '&nbsp;')
                            .replace(/[\u00A0]/g, ' ');
                    case 'object':
                        return rawValue === null ? null : rawValue.toString();
                }
                break;
            case DataType.DATE:
                if (rawValue instanceof DateTime) return rawValue;
                if (rawValue instanceof Date) {
                    const ret = new DateTime();
                    ret.set(rawValue);
                    return ret;
                }
                switch (rawValueType) {
                    case 'string': {
                        const res = rawValue.match(DataType.DATE_FORMAT);
                        if (res) {
                            // Validate, restore to normal form and build DateTime object
                            let year = res[1];
                            if (DataType.toNumber(year) < 1800) year = '1800';
                            const month = res[2];
                            const day = res[3];
                            const hours = res[4];
                            const minutes = res[5];
                            const seconds = res[6];
                            const millis = res[7];
                            const z = res[8];
                            let dateStr = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
                            if (millis) dateStr += `.${millis}`;
                            if (z) dateStr += z;
                            const ret = new DateTime();
                            const date = new Date(dateStr);
                            ret.set(date);
                            return ret;
                        }
                        throw `Unable to convert value '${rawValue === null ? 'null' : rawValue.toString()}' to type ${DataType.getTypeName(targetType)} (JS type: ${rawValueType})`;
                    }
                    default:
                        throw `Unable to convert value '${rawValue === null ? 'null' : rawValue.toString()}' to type ${DataType.getTypeName(targetType)} (JS type: ${rawValueType})`;
                }
            case DataType.PUBLISHEDDATE: {
                if (rawValue instanceof DateTime) return rawValue;
                if (rawValue instanceof Date) {
                    const ret = new DateTime();
                    ret.set(rawValue);
                    return ret;
                }
                const ret = new DateTime();
                const date = moment(rawValue, 'MM/D/YYYY hh:mm:ss a').toDate();
                ret.set(date);
                return ret;
            }
            case DataType.DISCOVERDATE: {
                if (rawValue instanceof DateTime) return rawValue;
                if (rawValue instanceof Date) {
                    const ret = new DateTime();
                    ret.set(rawValue);
                    return ret;
                }
                const ret = new DateTime();
                const date = moment(`${rawValue}Z`, 'YYYY-MM-DDThh:mm:ssZ').toDate();
                ret.set(date);
                return ret;
            }
            case DataType.OBJECT:
                return rawValue;
            case DataType.BOOL:
                switch (rawValueType) {
                    case 'number':
                        return rawValue !== 0;
                    case 'boolean':
                        return rawValue;
                    case 'string': {
                        const normalized = rawValue.trim().toLowerCase();
                        return normalized === 'true' || normalized === '1';
                    }
                    default:
                        throw `Unable to convert value '${rawValue === null ? 'null' : rawValue.toString()}' to type ${DataType.getTypeName(targetType)} (JS type: ${rawValueType})`;
                }
            default:
                throw `Convesion to type ${DataType.getTypeName(targetType)} not implemented`;
        }
    }
}
